<template>
  <v-app class="application">
    <!-- Migration Banner -->
    <v-expand-transition>
      <div v-if="showMigrationBanner" class="migration-banner">
        <div class="migration-content">
          <v-icon color="white" class="mr-2">mdi-information</v-icon>
          <span class="migration-text">
            <strong>Aus AlinoCam wird AlinoHub!</strong>
            Am 07.12.2025 wird AlinoCam für alle Nutzer durch AlinoHub ersetzt.
          </span>
          <v-btn
            color="white"
            class="migration-btn"
            @click="showVideo"
            outlined
          >
            <v-icon left class="mr-1">mdi-play-circle</v-icon>
            Video ansehen
          </v-btn>
          <v-btn
            color="white"
            class="migration-btn"
            @click="tryNewApp"
            outlined
          >
            Jetzt testen
          </v-btn>
          <v-btn
            color="white"
            class="migration-btn"
            @click="showConfirmDialog"
          >
            Jetzt migrieren
          </v-btn>
          <v-btn
            icon
            color="white"
            @click="dismissBanner"
            class="ml-2"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </div>
    </v-expand-transition>

    <!-- Video Dialog -->
    <v-dialog v-model="videoDialogVisible" max-width="900">
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center pa-4">
          <span class="text-h5">AlinoHub - Was ist neu?</span>
          <v-btn
            icon
            @click="closeVideo"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-0">
          <div class="video-container">
            <!-- YouTube embed -->
            <iframe
              v-if="videoDialogVisible"
              :src="videoUrl"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen
            ></iframe>
            
            <!-- Alternative: Direct video file -->
            <!-- <video v-if="videoDialogVisible" controls autoplay>
              <source :src="videoUrl" type="video/mp4">
              Your browser does not support the video tag.
            </video> -->
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Confirmation Dialog -->
    <v-dialog v-model="confirmDialogVisible" max-width="500">
      <v-card>
        <v-card-title class="text-h5 pa-4">
          <v-icon color="warning" class="mr-2">mdi-alert</v-icon>
          Zu AlinoHub wechseln?
        </v-card-title>
        <v-card-text class="pa-4">
          <p class="mb-3">
            Sie werden jetzt dauerhaft zu AlinoHub weitergeleitet.
          </p>
          <p class="mb-0">
            <strong>Hinweis:</strong> Diese Entscheidung kann nicht rückgängig gemacht werden. 
            Sie werden ab sofort immer automatisch zu AlinoHub weitergeleitet.
          </p>
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="confirmDialogVisible = false"
          >
            Abbrechen
          </v-btn>
          <v-btn
            color="primary"
            @click="migrateNow"
          >
            Jetzt wechseln
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-main style="background-color: #fcfcfc; height: 100vh">
      <router-view />
    </v-main>
  </v-app>
</template>

<script>
export default {
  name: 'App',
  data() {
    return {
      showMigrationBanner: false,
      migrationConfig: null,
      checkInterval: null,
      videoDialogVisible: false,
      confirmDialogVisible: false,
      // Change this to your video URL (YouTube, Vimeo, or direct video file)
      videoUrl: 'https://firebasestorage.googleapis.com/v0/b/alinocam-staging.appspot.com/o/Alinohub%20final%201.mp4?alt=media&token=c7cae077-3463-43ab-b9ee-95420451fe42',
    };
  },
  beforeCreate() {
    document.title = this.$route.meta.tab || this.$product;
  },
  async mounted() {
    await this.loadMigrationConfig();
    this.checkMigration();
    
    // Check every 5 minutes for config changes
    this.checkInterval = setInterval(() => {
      this.loadMigrationConfig();
      this.checkMigration();
    }, 5 * 60 * 1000);
  },
  beforeDestroy() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }
  },
  watch: {
    '$route.params.projectId': {
      handler: function (id) {
        if (!id || id == null) this.destroyProject();
      },
      deep: true,
      immediate: true,
    },
  },
  computed: {
    project() {
      return this.$store.getters.project;
    },
  },
  methods: {
    async loadMigrationConfig() {
      try {
        const response = await fetch('/migration-config.json');
        this.migrationConfig = await response.json();
      } catch (error) {
        console.error('Failed to load migration config:', error);
      }
    },
    
    checkMigration() {
      if (!this.migrationConfig) return;

      const now = new Date();
      const migrationDate = new Date(this.migrationConfig.migrationDate);
      const pastMigrationDate = now >= migrationDate;

      // Auto-enforce migration after migration date
      if (pastMigrationDate || this.migrationConfig.forceRedirectAll) {
        this.performRedirect();
        return;
      }

      // Check if user has opted in
      const hasOptedIn = localStorage.getItem('migration-opted-in') === 'true';
      if (hasOptedIn && this.migrationConfig.enableRedirect) {
        this.performRedirect();
        return;
      }

      // Check if banner should be shown
      const dismissedUntil = localStorage.getItem('migration-banner-dismissed');
      if (dismissedUntil) {
        const dismissedTime = parseInt(dismissedUntil);
        if (Date.now() < dismissedTime) {
          this.showMigrationBanner = false;
          return;
        }
      }

      this.showMigrationBanner = this.migrationConfig.showBanner && !pastMigrationDate;
    },

    performRedirect() {
      if (this.migrationConfig && this.migrationConfig.newUrl) {
        window.location.href = this.migrationConfig.newUrl;
      }
    },

    showVideo() {
      this.videoDialogVisible = true;
    },

    closeVideo() {
      this.videoDialogVisible = false;
    },

    showConfirmDialog() {
      this.confirmDialogVisible = true;
    },

    tryNewApp() {
      if (this.migrationConfig && this.migrationConfig.newUrl) {
        window.open(this.migrationConfig.newUrl, '_blank');
      }
    },

    migrateNow() {
      this.confirmDialogVisible = false;
      localStorage.setItem('migration-opted-in', 'true');
      this.performRedirect();
    },

    dismissBanner() {
      // Dismiss for 24 hours
      const dismissUntil = Date.now() + (24 * 60 * 60 * 1000);
      localStorage.setItem('migration-banner-dismissed', dismissUntil.toString());
      this.showMigrationBanner = false;
    },

    destroyProject() {
      this.$api.project.destroy();
    },
  },
};
</script>

<style scoped>
html,
body {
  margin: 0;
  height: 100%;
  overflow: hidden;
  user-drag: none !important;
}

.application {
  background-color: var(--v-background-base);
  -webkit-user-drag: none;
  overflow-y: hidden !important;
  user-drag: none !important;
}

/* Migration Banner Styles */
.migration-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: #cc2029;
  color: white;
  padding: 12px 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 9999;
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from {
    transform: translateY(-100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.migration-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
  max-width: 1200px;
  margin: 0 auto;
}

.migration-text {
  flex: 1;
  min-width: 300px;
  font-size: 14px;
  line-height: 1.5;
}

.migration-text strong {
  font-weight: 600;
  margin-right: 8px;
}

.migration-btn {
  white-space: nowrap;
}

/* Video Dialog Styles */
.video-container {
  position: relative;
  padding-bottom: 56.25%; /* 16:9 aspect ratio */
  height: 0;
  overflow: hidden;
  background: #000;
}

.video-container iframe,
.video-container video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
</style>

<style lang="scss">
@import './scss/variables.scss';

html,
body {
  margin: 0;
  height: 100%;
  overflow: hidden !important;
  font-family: 'Lato';
}

.application {
  overflow: hidden !important;
  font-family: $body-font-family, sans-serif !important;
  .title {
    font-family: $body-font-family, sans-serif !important;
  }
}
</style>

<style scss>
::-webkit-scrollbar {
    width: 13px;
    height: 13px;
    background-color: white;
}

::-webkit-scrollbar-track {
    background-color: #FAFAFA;
}

::-webkit-scrollbar-thumb {
    background: #A0A0A0;
    border-radius: 10px;
    margin-right: 5px;
    border: 2px solid #FAFAFA
}

::-webkit-scrollbar-thumb:hover {
    background: #808080;
}
</style>